import { isAborted, isClientFailureNotAuthorized, isRetryableFailure, isServerFailure, timeout } from '@art-suite/art-core-ts';
import { ErrorWithStatus, getErrorCommunicationStatusOrUndefined } from '@kaiizen/zod-less-api';
import type { ConcurrencyManager } from './createConcurrencyManager';
import { DEFAULT_BACKOFF_FACTOR, DEFAULT_MAX_RETRY_DELAY_MS, DEFAULT_MAX_RETRY_MS, DEFAULT_PRIORITY, DEFAULT_RETRY_DELAY_MS } from './defaults';

const JITTER_FACTOR = 0.1; // jitter +/- JITTER_FACTOR * 100%
const jitter = (ms: number) => ms * (1 - JITTER_FACTOR + Math.random() * JITTER_FACTOR * 2);

export const getRetryDelay = (baseMs: number, retryBackoffFactor: number, attempt: number, maxRetryDelayMs?: number) => {
  const delayMs = jitter(baseMs * Math.pow(retryBackoffFactor, attempt - 1));
  return Math.min(delayMs, maxRetryDelayMs ?? DEFAULT_MAX_RETRY_DELAY_MS) | 0;
};

type RetryStatus = {
  attempt: number;
  updatedAt: number;
  lastError?: unknown;
  cancelRetries: () => void;
};

/**
 * Classifies a terminal failure so consumers (and the default log policy) can pick a
 * sensible severity.
 *
 * - `authorization`: 401/403/407/451. NOT an infrastructure error — the request was
 *   well-formed and reached the server, the server made an authorization decision. Common
 *   benign cause: a stale request for the previous org races a user-initiated org switch.
 * - `aborted`: the request (or its retry loop) was cancelled. Expected, not a bug.
 * - `retryable`: transient failure (network/timeouts/etc) that exhausted retries. Worth a
 *   `warn` — possibly real, possibly transient.
 * - `other`: everything else (5xx exhausted, 4xx client bugs other than auth, unknown).
 *   These are the genuinely surprising terminal failures and warrant `error`.
 */
export type FinalFailureKind = 'authorization' | 'aborted' | 'retryable' | 'other';

export type FinalFailureInfo = {
  kind: FinalFailureKind;
  attempts: number;
  totalTimeMs: number;
  maxRetryMs: number | null;
  key?: string;
  message: string;
};

const classifyFinalFailure = (error: unknown): FinalFailureKind => {
  const status = getErrorCommunicationStatusOrUndefined(error);
  if (isAborted(status)) return 'aborted';
  if (isClientFailureNotAuthorized(status)) return 'authorization';
  if (isRetryableFailure(status)) return 'retryable';
  return 'other';
};

const defaultOnFinalError = (error: unknown, info: FinalFailureInfo): void => {
  // Default policy: noisy, but only for failures that *should* surprise an engineer.
  // Authorization and abort terminals are expected app-state outcomes — the application
  // owns handling them; the cache library should not pollute logs with them.
  switch (info.kind) {
    case 'authorization':
    case 'aborted':
      return; // silent by default; surface via onFinalError if a caller wants visibility
    case 'retryable':
      console.warn(info.message, error);
      return;
    case 'other':
      console.error(info.message, error);
      return;
  }
};

/***
 * Options for the requestWithRetry function.
 * @param key - The key of the request - only used for log tracing.
 * @param verbose - Whether to log verbose information.
 * @param maxRetryDelayMs - The maximum delay between retries in milliseconds.
 * This is the maximum delay between retries (before jitter, but after
 * retryDelayMs and retryBackoffFactor are applied). Use Infinity for no maximum
 * delay. See DEFAULT_MAX_RETRY_DELAY_MS
 * @param maxRetryMs - The maximum number of milliseconds to spend retrying the
 * request. Use Infinity for indefinite retries. Only counts time spent in
 * active requests plus retry delays. Time waiting in the queue or while paused is
 * excluded, ensuring retries aren't shortchanged by a busy concurrency manager.
 * @param retryDelayMs - The base delay between retries in milliseconds. This as
 * the base, initial retry delay. Jitter is applied to every delay. 2nd, 3rd etc
 * retries will multiply the base delay by retryBackoffFactor. See
 * DEFAULT_RETRY_DELAY_MS
 * @param retryBackoffFactor - The exponential backoff factor. The delay between
 * retries will be multiplied by this factor for each retry. See
 * DEFAULT_BACKOFF_FACTOR
 * @param priority - The priority of the request. When the request concurrency
 * limit is hit, higher priority items will get serviced first. Higher numbers
 * indicate higher priority. Negative priorities are allowed. When priorities
 * are equal, a newly queued request will go to the end of the list of requests
 * with the same priority. The default priority is 0.
 * @param retryServerFailures - Whether to retry server failures. See DEFAULT_RETRY_SERVER_FAILURES
 * @param shouldRetry - When provided, called with the error before each retry.
 * if shouldRetry returns:
 * - true - retry proceeds (delay then next attempt).
 * - false - no retry is attempted and the error is thrown.
 *
 * If shouldRetry is not provided, default logic is used:
 * - retry when there is no communication status (e.g. plain Error)
 * - retry if isRetryableFailure(communicationStatus) is true
 * - retry if retryServerFailures is set and isServerFailure(communicationStatus) is true
 */
export type RetryOptions = {
  // logging options
  key?: string;
  verbose?: boolean;

  // request management options
  maxRetryDelayMs?: number;
  maxRetryMs?: number;
  retryBackoffFactor?: number;
  retryDelayMs?: number;
  retryServerFailures?: boolean;
  priority?: number;
  shouldRetry?: (error: unknown) => boolean;

  /**
   * Called once when the retry loop terminates with a failure (i.e. the error is about to
   * be thrown to the caller). Use this to apply your own logging/telemetry policy. If
   * omitted, a built-in policy is used that silences expected app-state terminals
   * (`authorization`, `aborted`), warns on exhausted retryables, and errors on `other`.
   * Pass `() => {}` to silence everything.
   */
  onFinalError?: (error: unknown, info: FinalFailureInfo) => void;
};

export const requestWithRetry = async <T>(
  manager: ConcurrencyManager,
  mutationFunction: (retryStatus: RetryStatus) => Promise<T> | T,
  options?: RetryOptions,
): Promise<T> => {
  const maxRetryMs = options?.maxRetryMs ?? DEFAULT_MAX_RETRY_MS;
  const priority = options?.priority ?? DEFAULT_PRIORITY;
  const retryDelayMs = options?.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS;
  const retryBackoffFactor = options?.retryBackoffFactor ?? DEFAULT_BACKOFF_FACTOR;
  const maxRetryDelayMs = options?.maxRetryDelayMs ?? DEFAULT_MAX_RETRY_DELAY_MS;
  let active = true;
  let aborted = false;
  let totalTimeMs = 0; // total time actually waiting for retries; time while the concurrency manager is paused does not count
  const getLogMessage = (message: string) =>
    `QueryCache ${message} Total time: ${Math.floor(totalTimeMs / 1000)}s of ${maxRetryMs == null ? 'indefinitely' : Math.floor(maxRetryMs / 1000)}s for key=${
      options?.key ?? 'unknown'
    }`;
  const retryStatus: RetryStatus = {
    attempt: 0,
    updatedAt: Date.now(),
    lastError: undefined,
    cancelRetries: () => {
      if (options?.verbose) console.info(getLogMessage(`cancelled after ${retryStatus.attempt} retries (verbose=true)`), retryStatus.lastError);
      active = false;
      aborted = true;
    },
  };

  while (active && (maxRetryMs === null || retryStatus.attempt === 0 || totalTimeMs < maxRetryMs)) {
    try {
      return await manager.enqueue(async () => {
        const startTime = Date.now();
        try {
          return await mutationFunction(retryStatus);
        } finally {
          totalTimeMs += Date.now() - startTime;
        }
      }, priority);
    } catch (error) {
      retryStatus.updatedAt = Date.now();
      retryStatus.lastError = error;
      retryStatus.attempt++;

      const communicationStatus = getErrorCommunicationStatusOrUndefined(error);
      const doRetry = options?.shouldRetry
        ? options.shouldRetry(error)
        : !communicationStatus || (isRetryableFailure(communicationStatus) || (options?.retryServerFailures && isServerFailure(communicationStatus)));

      if (doRetry) {
        if (options?.verbose) console.log(getLogMessage(`retrying: ${communicationStatus ?? 'unknown communication status for: ' + error}`));
      } else {
        active = false;
        break;
      }
      // Check if we've exceeded maxRetryMs BEFORE waiting for the delay
      if (maxRetryMs !== null && totalTimeMs >= maxRetryMs) break;

      const delayMs = getRetryDelay(retryDelayMs, retryBackoffFactor, retryStatus.attempt, maxRetryDelayMs ?? undefined);
      if (options?.verbose) console.log(getLogMessage(`retrying in ${Math.floor(delayMs / 1000)}s`));
      await timeout(delayMs);
      totalTimeMs += delayMs;
    }
  }
  if (aborted) throw new ErrorWithStatus('Request aborted', 'aborted');

  const finalError = retryStatus.lastError;
  const info: FinalFailureInfo = {
    kind: classifyFinalFailure(finalError),
    attempts: retryStatus.attempt,
    totalTimeMs,
    maxRetryMs: maxRetryMs ?? null,
    key: options?.key,
    message: getLogMessage(`failed after ${retryStatus.attempt} retries`),
  };
  (options?.onFinalError ?? defaultOnFinalError)(finalError, info);
  throw finalError;
};
